import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createLabel } from '@/services/workflow/create-label';
import { ORG_LABELS_QUERY_KEY } from '@/hooks/queries/use-get-org-labels';
import { useOrgStore } from '@/stores/org-store';
import { Label } from '@/types/workflow-types';
import * as Sentry from '@sentry/react';

interface CreateOrgLabelInput {
  name: string;
  color?: string;
  description?: string;
}

export function useCreateOrgLabel() {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const queryClient = useQueryClient();

  return useMutation<Label, Error, CreateOrgLabelInput>({
    mutationFn: async (input) => {
      if (!orgId) throw new Error('No org ID');
      const response = await createLabel(orgId, input);
      if (response.detail || !response.data) {
        throw new Error(response.detail || 'Failed to create label');
      }
      return response.data;
    },
    onSuccess: (label) => {
      // Seed the cache with the new label so callers can immediately assign it
      // (before the background refetch lands), then reconcile with the server.
      queryClient.setQueryData<Label[]>(
        [ORG_LABELS_QUERY_KEY, orgId],
        (prev) =>
          prev?.some((l) => l.id === label.id)
            ? prev
            : [...(prev ?? []), label],
      );
      queryClient.invalidateQueries({ queryKey: [ORG_LABELS_QUERY_KEY] });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        extra: { context: 'create-org-label' },
      });
    },
  });
}
